import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { Cantiere, Lotto, DpacClient, AlgorithmLabel, Cluster } from '../types';
import ActionMenu, { ActionItem } from '../components/ActionMenu';
import { useNotification } from '../components/NotificationContext';

interface AdminCantieriProps {
  onSelectCantiere: (cantiere: Cantiere) => void;
}

const AdminCantieri: React.FC<AdminCantieriProps> = ({ onSelectCantiere }) => {
  const [cantieri, setCantieri] = useState<Cantiere[]>([]);
  const [editingCantiere, setEditingCantiere] = useState<Cantiere | null>(null);
  const { notify } = useNotification();
  const [managingLottiFor, setManagingLottiFor] = useState<Cantiere | null>(null);
  const [lotti, setLotti] = useState<Lotto[]>([]);
  const [editingLotto, setEditingLotto] = useState<Lotto | null>(null);
  const [dpacClients, setDpacClients] = useState<DpacClient[]>([]);
  const [algorithms, setAlgorithms] = useState<AlgorithmLabel[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);

  const lotTypes = ['BIB-001', 'BIB-001M', 'BIB-002', 'BIB-003', 'BIB-008', 'ARC-001', 'ARC-002', 'ARC-003', 'ARC-004', 'ARC-004_AS', 'ARC-005', 'ABAP-003'];
  const lotStatuses = ['In lavorazione', 'Completo', 'Collaudato', 'Rifiutato'];

  const initialFormState = {
    name: '',
    cluster_id: '',
    original_repo_dir: '',
    packaged_repo_dir: '',
    transient_repo_dir: '',
    creator: 'Ministero della Cultura',
    ipower1: 'Ministero della Cultura',
    ipower2: '',
    custodian: '',
    email: '',
    conservative_id: '',
    conservative_id_authority: '',
    relation_id: 'representation',
    collection: '',
    publisher: '',
    producer: '',
    publication_place: '',
    dpac_code: '',
    region_code: '',
    digital_resource_value: 0.15,
    algorithm_label_id: '',
  };

  const initialLottoState = {
    name: '',
    type: 'BIB-001' as any,
    expected_packages: 0,
    status: 'In lavorazione' as any,
    dpac_client_id: '' as any,
  };

  const [formData, setFormData] = useState(initialFormState);
  const [lottoFormData, setLottoFormData] = useState(initialLottoState);
  const [message, setMessage] = useState(''); // Keep local for internal use if needed, but we'll use notify

  useEffect(() => {
    fetchCantieri();
    fetchDpacClients();
    fetchAlgorithms();
    fetchClusters();
  }, []);

  const fetchClusters = async () => {
    try {
      const data = await api.get('/admin/clusters');
      setClusters(data.clusters || []);
    } catch (error: any) {
      console.error('Failed to fetch clusters.', error);
    }
  };

  const fetchCantieri = async () => {
    try {
      const data = await api.get('/admin/cantieri');
      setCantieri(data.cantieri);
    } catch (error: any) {
      notify(error.message || 'Failed to fetch cantieri.', 'error');
    }
  };

  const fetchDpacClients = async () => {
    try {
      const data = await api.get('/admin/dpac-clients');
      setDpacClients(data.clients || []);
      
      // Set default client if adding a new lot and clients are available
      if (data.clients && data.clients.length > 0 && !lottoFormData.dpac_client_id) {
        const defaultClient = data.clients.find((c: any) => c.name === 'Default Client DPAC');
        if (defaultClient) {
          setLottoFormData(prev => ({ ...prev, dpac_client_id: defaultClient.id }));
        }
      }
    } catch (error: any) {
      notify(error.message || 'Failed to fetch DPAC clients.', 'error');
    }
  };

  const fetchAlgorithms = async () => {
    try {
      const data = await api.get('/admin/algorithm-labels');
      setAlgorithms(data.algorithm_labels || []);
    } catch (error: any) {
      notify(error.message || 'Failed to fetch algorithm labels.', 'error');
    }
  };

  const fetchLotti = async (cantiereId: number) => {
    try {
      const data = await api.get(`/admin/cantieri/${cantiereId}/lotti`);
      setLotti(data.lotti);
    } catch (error: any) {
      notify(error.message || 'Failed to fetch lotti.', 'error');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const val = type === 'number' ? parseFloat(value) : value;
    setFormData({ ...formData, [name]: val });
  };

  const handleLottoInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setLottoFormData({ ...lottoFormData, [name]: value });
  };

  const handleEdit = (cantiere: Cantiere) => {
    setManagingLottiFor(null);
    setEditingCantiere(cantiere);
    setFormData({
      name: cantiere.name,
      cluster_id: cantiere.cluster_id?.toString() || '',
      original_repo_dir: cantiere.original_repo_dir,
      packaged_repo_dir: cantiere.packaged_repo_dir,
      transient_repo_dir: cantiere.transient_repo_dir || '',
      creator: cantiere.creator || 'Ministero della Cultura',
      ipower1: cantiere.ipower1 || 'Ministero della Cultura',
      ipower2: cantiere.ipower2 || '',
      custodian: cantiere.custodian || '',
      email: cantiere.email || '',
      conservative_id: cantiere.conservative_id || '',
      conservative_id_authority: cantiere.conservative_id_authority || '',
      relation_id: cantiere.relation_id || 'representation',
      collection: cantiere.collection || '',
      publisher: cantiere.publisher || '',
      producer: cantiere.producer || '',
      publication_place: cantiere.publication_place || '',
      dpac_code: cantiere.dpac_code || '',
      region_code: cantiere.region_code || '',
      digital_resource_value: cantiere.digital_resource_value ?? 0.15,
      algorithm_label_id: cantiere.algorithm_label_id?.toString() || '',
    });
  };

  const handleManageLotti = (cantiere: Cantiere) => {
    setEditingCantiere(null);
    setManagingLottiFor(cantiere);
    setEditingLotto(null);
    
    // Set initial lot form state with default client if available
    const defaultClient = dpacClients.find(c => c.name === 'Default Client DPAC');
    setLottoFormData({ 
      ...initialLottoState, 
      dpac_client_id: defaultClient ? defaultClient.id : (dpacClients.length > 0 ? dpacClients[0].id : '')
    });
    
    fetchLotti(cantiere.id);
  };

  const handleDelete = async (cantiereId: number) => {
    if (window.confirm('Are you sure you want to delete this cantiere?')) {
      try {
        await api.delete(`/admin/cantieri/${cantiereId}`);
        notify('Cantiere eliminato con successo!', 'success');
        fetchCantieri();
      } catch (error: any) {
        notify(error.message || 'Errore eliminazione cantiere.', 'error');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        algorithm_label_id: formData.algorithm_label_id === '' ? null : parseInt(formData.algorithm_label_id, 10),
        cluster_id: formData.cluster_id === '' ? null : parseInt(formData.cluster_id, 10)
      };

      if (editingCantiere) {
        await api.put(`/admin/cantieri/${editingCantiere.id}`, payload);
        notify('Cantiere aggiornato con successo!', 'success');
      } else {
        await api.post('/admin/cantieri', payload);
        notify('Cantiere creato con successo!', 'success');
      }
      setFormData(initialFormState);
      setEditingCantiere(null);
      fetchCantieri();
    } catch (error: any) {
      notify(error.message || 'Errore salvataggio cantiere.', 'error');
    }
  };

  const handleLottoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!managingLottiFor) return;
    
    if (!lottoFormData.dpac_client_id) {
      notify('La scelta del Client DPAC è obbligatoria.', 'error');
      return;
    }

    try {
      if (editingLotto) {
        await api.put(`/admin/lotti/${editingLotto.id}`, lottoFormData);
        notify('Lotto aggiornato con successo!', 'success');
      } else {
        await api.post('/admin/lotti', { ...lottoFormData, cantiere_id: managingLottiFor.id });
        notify('Lotto creato con successo!', 'success');
      }
      
      const defaultClient = dpacClients.find(c => c.name === 'Default Client DPAC');
      setLottoFormData({
        ...initialLottoState,
        dpac_client_id: defaultClient ? defaultClient.id : (dpacClients.length > 0 ? dpacClients[0].id : '')
      });
      setEditingLotto(null);
      fetchLotti(managingLottiFor.id);
    } catch (error: any) {
      notify(error.message || 'Errore salvataggio lotto.', 'error');
    }
  };


  const handleDeleteLotto = async (lottoId: number) => {
    if (!managingLottiFor) return;
    if (window.confirm('Sicuro di voler eliminare questo lotto?')) {
      try {
        await api.delete(`/admin/lotti/${lottoId}`);
        notify('Lotto eliminato!', 'success');
        fetchLotti(managingLottiFor.id);
      } catch (error: any) {
        notify(error.message || 'Errore eliminazione.', 'error');
      }
    }
  };

  return (
    <div className="container">
      {!managingLottiFor ? (
        <>
          <div className="card">
            <h2 style={{ marginBottom: '24px' }}>{editingCantiere ? 'Edit Cantiere' : 'Nuovo Cantiere'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Cantiere Name</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div className="form-group">
                  <label>Original Repository Path</label>
                  <input type="text" name="original_repo_dir" value={formData.original_repo_dir} onChange={handleInputChange} required />
                </div>
                <div className="form-group">
                  <label>Packaged Repository Path</label>
                  <input type="text" name="packaged_repo_dir" value={formData.packaged_repo_dir} onChange={handleInputChange} required />
                </div>
                <div className="form-group">
                  <label>Transient Repository Path</label>
                  <input type="text" name="transient_repo_dir" value={formData.transient_repo_dir} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Cluster</label>
                  <select name="cluster_id" value={formData.cluster_id} onChange={handleInputChange}>
                    <option value="">(Nessun Cluster)</option>
                    {clusters.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid rgba(0,0,0,0.1)' }} />
              <h3 style={{ marginBottom: '16px', fontSize: '1.1rem' }}>Configurazione Metadata</h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div className="form-group">
                  <label>Creator</label>
                  <input type="text" name="creator" value={formData.creator} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>IPower 1</label>
                  <input type="text" name="ipower1" value={formData.ipower1} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>IPower 2</label>
                  <input type="text" name="ipower2" value={formData.ipower2} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Custodian</label>
                  <input type="text" name="custodian" value={formData.custodian} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Conservative ID</label>
                  <input type="text" name="conservative_id" value={formData.conservative_id} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Conservative ID Authority</label>
                  <input type="text" name="conservative_id_authority" value={formData.conservative_id_authority} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Relation ID</label>
                  <input type="text" name="relation_id" value={formData.relation_id} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Collection</label>
                  <input type="text" name="collection" value={formData.collection} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Publisher</label>
                  <input type="text" name="publisher" value={formData.publisher} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Produttore</label>
                  <input type="text" name="producer" value={formData.producer} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Luogo di pubblicazione</label>
                  <input type="text" name="publication_place" value={formData.publication_place} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Codice DPAC</label>
                  <input type="text" name="dpac_code" value={formData.dpac_code} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Codice Regione</label>
                  <input type="text" name="region_code" value={formData.region_code} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Valore risorsa digitale (€)</label>
                  <input type="number" step="0.01" name="digital_resource_value" value={formData.digital_resource_value} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Algoritmo Generazione Label</label>
                  <select name="algorithm_label_id" value={formData.algorithm_label_id} onChange={handleInputChange}>
                    <option value="">(Nessuno - usa default)</option>
                    {algorithms.map(algo => (
                      <option key={algo.id} value={algo.id}>{algo.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button type="submit" className="btn btn-primary">{editingCantiere ? 'Salva Modifiche' : 'Crea Cantiere'}</button>
                {editingCantiere && (
                  <button
                    type="button"
                    className="btn btn-text"
                    onClick={() => { setEditingCantiere(null); setFormData(initialFormState); }}
                  >
                    Annulla
                  </button>
                )}
              </div>
            </form>
          </div>

          <div className="card">
            <h3>Lista Cantieri</h3>
            <table className="table-responsive">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Cluster</th>
                  <th>Proprietario (Creator)</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {cantieri.map((cantiere) => (
                  <tr key={cantiere.id}>
                    <td>
                      <strong>{cantiere.name}</strong>
                      <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>O: {cantiere.original_repo_dir}</div>
                    </td>
                    <td>
                      {cantiere.cluster_name ? (
                        <span style={{ fontWeight: '500', color: 'var(--primary-color)' }}>{cantiere.cluster_name}</span>
                      ) : (
                        <span style={{ opacity: 0.4 }}>-</span>
                      )}
                    </td>
                    <td>{cantiere.creator}</td>
                    <td>
                      <ActionMenu actions={[
                        { label: 'Modifica Dati', icon: 'fas fa-pen-to-square', onClick: () => handleEdit(cantiere) },
                        { label: 'Gestione Lotti', icon: 'fas fa-box', onClick: () => handleManageLotti(cantiere) },
                        { label: 'Esplora', icon: 'fas fa-magnifying-glass', onClick: () => onSelectCantiere(cantiere) },
                        { label: 'Elimina', icon: 'fas fa-trash-can', onClick: () => handleDelete(cantiere.id), color: 'var(--error-color)' },
                      ]} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2>Gestione Lotti: {managingLottiFor.name}</h2>
              <button className="btn btn-text" onClick={() => setManagingLottiFor(null)}>Chiudi</button>
            </div>

            <form onSubmit={handleLottoSubmit} style={{ background: 'rgba(0,0,0,0.02)', padding: '20px', borderRadius: '8px' }}>
              <h3 style={{ marginBottom: '16px' }}>{editingLotto ? 'Modifica Lotto' : 'Nuovo Lotto'}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.5fr 1fr 1fr', gap: '16px', alignItems: 'end' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Nome Lotto</label>
                  <input type="text" name="name" value={lottoFormData.name} onChange={handleLottoInputChange} required />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Tipo</label>
                  <select name="type" value={lottoFormData.type} onChange={handleLottoInputChange}>
                    {lotTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Client DPAC</label>
                  <select name="dpac_client_id" value={lottoFormData.dpac_client_id} onChange={handleLottoInputChange} required>
                    <option value="">Seleziona Client...</option>
                    {dpacClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Pacchetti Previsti</label>
                  <input type="number" name="expected_packages" value={lottoFormData.expected_packages} onChange={handleLottoInputChange} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Stato</label>
                  <select name="status" value={lottoFormData.status} onChange={handleLottoInputChange}>
                    {lotStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ marginTop: '16px', display: 'flex', gap: '10px' }}>
                <button type="submit" className="btn btn-primary">{editingLotto ? 'Salva' : 'Aggiungi Lotto'}</button>
                {editingLotto && <button type="button" className="btn btn-text" onClick={() => { setEditingLotto(null); setLottoFormData(initialLottoState); }}>Annulla</button>}
              </div>
            </form>

            <table className="table-responsive" style={{ marginTop: '24px' }}>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Tipo</th>
                  <th>Client DPAC</th>
                  <th>Pacchetti</th>
                  <th>Stato</th>
                  <th>Azioni</th>
                </tr>

              </thead>
              <tbody>
                {lotti.map(l => (
                  <tr key={l.id}>
                    <td><strong>{l.name}</strong></td>
                    <td>{l.type}</td>
                    <td><span style={{ fontSize: '0.85rem', color: 'var(--primary-color)' }}>{l.dpac_client_name}</span></td>
                    <td>{l.expected_packages}</td>
                    <td>{l.status}</td>

                    <td>
                      <ActionMenu actions={[
                        { label: 'Modifica', icon: 'fas fa-pen-to-square', onClick: () => { setEditingLotto(l); setLottoFormData(l as any); } },
                        { label: 'Elimina', icon: 'fas fa-trash-can', onClick: () => handleDeleteLotto(l.id), color: 'var(--error-color)' },
                      ]} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {lotti.length === 0 && <p style={{ textAlign: 'center', padding: '20px', color: 'rgba(0,0,0,0.4)' }}>Nessun lotto definito.</p>}
          </div>
        </>
      )}
      {/* Removed local message display, replaced by global notify */}
    </div>
  );
};

export default AdminCantieri;